import { sealingService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { proceedOnlyIfJobIsUnlocked } from '../modules/jobLocking';
import { getDocumentsAsSortedArray } from '../selectors/documents';
import { documentIndexChangesApplied, SanitizedDocument } from '../store/documents';
import { documentIndexChanged, documentToolsShown, IndexMap } from '../store/documentTools';
import { showPopup } from '../store/popup';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export function generateIndexMap(documents: readonly SanitizedDocument[]): IndexMap {
  const indexMap: { [pdfId: number]: number } = {};
  for (const document of documents) {
    indexMap[document.id] = document.index;
  }

  return indexMap;
}

export function showDocumentTools(): ThunkActionVoid {
  return (dispatch, getState) => {
    proceedOnlyIfJobIsUnlocked();

    const documents = getDocumentsAsSortedArray(getState());
    const indexMap = generateIndexMap(documents);

    dispatch(documentToolsShown({ indexMap }));
    dispatch(showPopup('DOCUMENT_TOOLS'));
  };
}

export function moveDocumentToIndex(documentId: number, newIndex: number) {
  return documentIndexChanged({ documentId, newIndex });
}

export function applyIndexChanges(): AsyncThunkActionVoid {
  return (dispatch, getState) => {
    const state = getState();
    const oldIndexMap = generateIndexMap(getDocumentsAsSortedArray(state));

    const { indexMap } = state.documentTools;
    dispatch(applyIndexMap(indexMap!));

    const spinnerId = dispatch(addSpinnerMessage('Reindexing documents...'));

    return sealingService.jobs
      .reindexPdfs(state.job.jobId, indexMap)
      .catch(() => dispatch(applyIndexMap(oldIndexMap)))
      .then(() => {
        dispatch(removeSpinnerMessage(spinnerId));
      });
  };
}

export function applyIndexMap(indexMap: IndexMap) {
  return documentIndexChangesApplied({ indexMap });
}

export const reindexPdfsNaturalSort = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  try {
    const indexMap = await sealingService.jobs.patchReindexPdfsNaturalSort(getState().job.id);
    dispatch(applyIndexMap(indexMap));
  } catch (error) {
    dispatch(enqueueError('Error reindexing documents.'));
    console.error(error);
  }
};
